/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';
import { AppContext } from '@com/core/common/contexts/appContext';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import MediaPreloader from '@com/core/common/components/mediaPreloader';
import dailyConfig from '@com/core/noon/dailyConfig';
import MapMarker from './MapMarker';
import dataAeEn from './data-ae-en.json';
import dataAeAr from './data-ae-ar.json';
import dataSaEn from './data-sa-en.json';
import dataSaAr from './data-sa-ar.json';

import SearchBox from './SearchBox';
import TypingIntro from './TypingIntro';

const LandingPage = (): JSX.Element => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const { customer, isCustomerLoading, setInitialLoginScreen, showLoginModal } = useContext(CustomerContext);
    const [activeMarker, setActiveMarker] = useState(1);

    const dataMap = {
        ae: {
            en: dataAeEn,
            ar: dataAeAr,
        },
        sa: {
            en: dataSaEn,
            ar: dataSaAr,
        },
    };
    const data = dataMap[locale.apiRegion][locale.lang];

    useEffect(() => {
        let timer = 0;
        timer = setTimeout(() => {
            if (activeMarker === data.markers.length) {
                setActiveMarker(1);
            } else {
                setActiveMarker(activeMarker + 1);
            }
        }, 6000);

        return () => clearTimeout(timer);
    }, [activeMarker]);

    return (
        <Container>
            <Hero style={{ backgroundImage: `url(${data.map})` }}>
                <div>
                    <div className="siteWidthContainer">
                        {data.typing && <TypingIntro heading={data.typing.heading} messages={data.typing.options} />}
                        <p className="intro">
                            {data.intro.map(str => (
                                <span key={str}>{str}</span>
                            ))}
                        </p>
                        <div className="searchContainer">
                            <SearchBox />
                        </div>
                        {!isCustomerLoading && !customer && (
                            <p className="loginHelper">
                                {t('already-a-daily-customer')}{' '}
                                <button
                                    type="button"
                                    onClick={(): void => {
                                        setInitialLoginScreen('login');
                                        showLoginModal(true);
                                    }}>
                                    {t('sign-in-here')}
                                </button>
                            </p>
                        )}
                    </div>
                </div>

                <div className="mapArea">
                    {data.markers.map((marker, index) => (
                        <MapMarker key={marker.name} data={marker} isActive={index + 1 === activeMarker} />
                    ))}
                </div>

                <div className="siteWidthContainer">
                    <HighlightsRow>
                        {data.highlights.map(highlight => (
                            <div className="infoBlock" key={highlight.title}>
                                <img src={highlight.img} alt={highlight.title} />
                                <p className="infoTitle">{highlight.title}</p>
                                <p className="infoContent">{highlight.desc}</p>
                            </div>
                        ))}
                    </HighlightsRow>
                </div>
            </Hero>

            <InfoSection>
                <div className="siteWidthContainer">
                    <section>
                        <h2>{data.screenshot.title}</h2>
                        <p className="subtitle">
                            {data.screenshot.subtitle.map(str => (
                                <span key={str}>{str}</span>
                            ))}
                        </p>
                        <img src={data.screenshot.img} alt={data.screenshot.title} />
                    </section>
                    <section>
                        <h2>{data.sellers.title}</h2>
                        <ul className="sellersList">
                            {data.sellers.list.map(seller => (
                                <li key={seller.name}>
                                    <MediaPreloader ratio={104.047619047619048}>
                                        <img src={seller.img} alt={seller.name} />
                                    </MediaPreloader>
                                </li>
                            ))}
                        </ul>
                    </section>
                    <AppDownloadSection>
                        <div className="contentBg">
                            <div className="content">
                                <h3>{data.app.title}</h3>
                                {data.app.content.map((p, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <p key={index}>{p}</p>
                                ))}
                                <div className="downloadRow">
                                    <a href={dailyConfig.urls.appStore} target="_blank" rel="noopener noreferrer">
                                        <img src={`/images/app-ios-${locale.lang}.svg`} alt="Install IOS App" />
                                    </a>
                                    <a href={dailyConfig.urls.googlePlay} target="_blank" rel="noopener noreferrer">
                                        <img src={`/images/app-android-${locale.lang}.svg`} alt="Install Android App" />
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="screenshotContainer">
                            <img src={data.app.img} alt="App Screenshots" />
                        </div>
                    </AppDownloadSection>
                </div>
            </InfoSection>
        </Container>
    );
};

const Container = styled.div`
    text-align: center;

    .siteWidthContainer {
        padding: 0 60px;
        max-width: 1000px;
    }
`;
const Hero = styled.section`
    margin-top: 136px; // 64 + 72 for fixed header
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 100% auto;
    height: 800px;
    display: flex;
    flex-direction: column;

    p.intro {
        font-size: 1.25rem;

        span {
            display: block;
        }
    }

    .searchContainer {
        position: relative;
        margin: 32px auto 24px;
        text-align: initial;
        max-width: 560px;
    }

    .loginHelper {
        font-size: 0.9375rem;
        color: ${({ theme }): string => theme.colors.bodyMid};

        button {
            color: ${({ theme }): string => theme.colors.body};
            text-decoration: underline;
            font-weight: 600;

            &:hover {
                text-decoration: none;
            }
        }
    }

    .mapArea {
        flex: 1;
        position: relative;
        width: 100%;
        max-width: 1280px;
        align-self: center;
    }
`;

const HighlightsRow = styled.div`
    display: flex;
    margin-left: -64px;

    > div {
        flex: 1;
        padding-left: 64px;
    }

    img {
        width: 100%;
        max-width: 210px;
        margin: 0 auto;
    }

    .infoTitle {
        font-weight: bold;
        margin-bottom: 4px;
    }

    .infoContent {
        font-size: 0.875rem;
        color: ${({ theme }): string => theme.colors.bodyMid};
    }
`;

const InfoSection = styled.div`
    margin-top: 80px;
    border-top: 1px solid ${({ theme }): string => theme.colors.border};
    padding: 72px 0 80px;

    section + section {
        margin-top: 96px;
    }

    h2 {
        font-size: 2rem;
        font-weight: bold;
        margin-bottom: 14px;
    }

    p.subtitle {
        font-size: 1.125rem;
        color: ${({ theme }): string => theme.colors.bodyMid};
        margin-bottom: 24px;

        span {
            display: block;
        }
    }

    .sellersList {
        display: flex;
        margin-left: -12px;
        margin-top: 24px;

        li {
            flex: 1;
            padding-left: 12px;
        }
    }
`;

const AppDownloadSection = styled.div`
    text-align: initial;
    margin-top: 64px;
    display: flex;
    justify-content: flex-end;
    position: relative;

    .contentBg {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #dfffe6;

        .content {
            width: 50%;
            padding: ${({ theme }): string => (theme.isRTL ? '40px 40px 46px 0' : '40px 0 46px 40px')};
        }
    }

    h3 {
        font-size: 1.5rem;
        font-weight: bold;
        margin-bottom: 10px;
    }

    p {
        font-size: 0.9375rem;
        line-height: 1.5;

        + p {
            margin-top: 12px;
        }
    }

    .downloadRow {
        display: flex;
        margin-top: 24px;

        a {
            display: block;
        }

        a + a {
            ${({ theme: { left } }): string => `margin-${left}: 12px`};
        }

        a:hover {
            opacity: 0.7;
        }

        img {
            height: 40px; /* apple guidelines */
        }
    }

    .screenshotContainer {
        flex: 0 0 50%;
        position: relative;
        z-index: 10;
    }
`;

export default LandingPage;
